import { environmentDetails } from '@bpd-library/utilities';
import { LOADABLE_MODAL_HOOK, NAMED_MODALS } from '@constants/modals';
import { modalController } from '@molecules/modal';
import { videoLoader } from '@molecules/video/utilities';
import { IEModalController } from '@organisms/ie-modal/utilities/ie-modal-controller';
import { iframeModalController } from '@organisms/iframe-modal/utilities/iframe-modal-controller';
import { GTMTriggerController } from '@source/utilities/gtm-trigger';
import { checkLoggedInUser } from '@store/user';
import environment from '@utilities/environment';
import { AREA_PAGE_MOCKS, prefillAreaDataMock } from '@utilities/environment-mock-data';
import { inViewController } from '@utilities/inview-controller';
import { Loadable } from '@utilities/loadable';
import { LoadableModal } from '@utilities/loadable-modal';
import { serviceConsentTracker } from '@utilities/service-consent-tracker';

const loader = () => {
    /*
        Listen to Usercentrics events
    */
    serviceConsentTracker.init();

    /*
        Initialize shared repo environment details
    */
    if (environment.isLocal) {
        AREA_PAGE_MOCKS.DeErven.TEST({ hasWishlist: true });
        prefillAreaDataMock();
    }

    environmentDetails.init({
        environment: window.__ENVIRONMENT_DETAILS__.environment,
        apiBaseUrl: window.__ENVIRONMENT_DETAILS__.apiBaseUrl,
        languageIsoCode: window.__ENVIRONMENT_DETAILS__.languageIsoCode,
        mapboxAccessToken: window.__ENVIRONMENT_DETAILS__.mapbox.accessToken,
        mapboxStyle: window.__ENVIRONMENT_DETAILS__.mapbox.style,
        areaId: window.__ENVIRONMENT_DETAILS__.areaId,
    });

    /*
        Call controllers
    */
    modalController();
    iframeModalController();
    IEModalController();
    checkLoggedInUser();
    inViewController();
    videoLoader();
    GTMTriggerController();

    /*
        Init dynamically loaded components
    */
    Loadable({
        hook: 'apartments-view',
        loader: () => import('@source/components/molecules/apartments-view'),
    });

    Loadable({
        hook: 'bpd-properties',
        loader: () => import('@features/properties'),
    });

    Loadable({
        hook: 'bpd-map-current-area',
        loader: () => import('@source/components/molecules/map-current-area'),
    });

    Loadable({
        hook: 'bpd-wishlist',
        loader: () => import('@features/wishlist/wishlist'),
    });

    Loadable({
        hook: 'bpd-house-plans',
        loader: () => import('@organisms/house-plans/house-plans'),
    });

    const { loadManually: loadAreaSubscription } = Loadable({
        hook: 'bpd-area-subscription',
        loader: () => import('@features/area-subscription'),
    });

    Loadable({
        hook: 'area-subscription-de',
        loader: () => import('@molecules/area-subscription-de/utilities/area-subscription-de'),
    });

    Loadable({
        hook: 'bpd-compare',
        loader: () => import('@features/compare'),
    });

    Loadable({
        hook: 'smart-subscription',
        loader: () => import('@molecules/smart-subscription'),
    });

    Loadable({
        hook: 'hero-apartment',
        loader: () => import('@organisms/hero-apartment'),
    });

    Loadable({
        hook: 'message-form-inline',
        loader: () => import('@organisms/message-form-inline/utilities/message-form-inline'),
    });

    Loadable({
        hook: 'bpd-flyin-update',
        loader: () => import('@molecules/flyin-update/flyin-update'),
    });

    Loadable({
        hook: 'error-page',
        loader: () => import('@organisms/error-page'),
    });

    Loadable({
        hook: 'bpd-faq',
        loader: () => import('@organisms/faq/faq'),
    });

    Loadable({
        hook: 'area-map',
        loader: () => import('@features/area-map'),
    });

    Loadable({
        hook: 'virtual-tour-wrapper',
        loader: () => import('@source/features/virtual-tour'),
    });

    Loadable({
        hook: 'house-detailpage-cta',
        loader: () => import('@molecules/house-detailpage-cta/house-detailpage-cta'),
    });

    /*
        Init dynamically loaded modals
    */
    LoadableModal({
        name: NAMED_MODALS.MORTGAGE_TOOL,
        loader: () => import('@features/mortgage-tool'),
    });

    LoadableModal({
        name: NAMED_MODALS.LEAD_FORM,
        loader: () => import('@organisms/lead-form'),
    });

    LoadableModal({
        name: NAMED_MODALS.BROCHURE_FORM,
        loader: () => import('@organisms/brochure-form'),
    });

    LoadableModal({
        name: NAMED_MODALS.NEWSLETTER_FORM,
        loader: () => import('@organisms/newsletter-form'),
    });

    LoadableModal({
        name: NAMED_MODALS.MESSAGE_FORM,
        loader: () => import('@organisms/message-form-modal'),
    });

    LoadableModal({
        name: NAMED_MODALS.HOUSING_MODULE,
        loader: () => import('@organisms/housing-module'),
    });

    LoadableModal({
        name: NAMED_MODALS.MORTGAGE_TOOL_RABO,
        loader: () => import('@organisms/mortgage-tool-rabo'),
    });

    /*
        Init unnamed dynamically loaded modals
        - NOTE: safe assumption is that unnamed modals must be iframes.
    */
    const knownModalNames = Object.values(NAMED_MODALS) as string[];
    document.querySelectorAll(`[${LOADABLE_MODAL_HOOK}]`).forEach((loader) => {
        const modalId = loader.getAttribute(`${LOADABLE_MODAL_HOOK}`);
        if (!modalId || knownModalNames.includes(modalId)) return;

        LoadableModal({ name: modalId, htmlTag: 'bpd-iframe-modal' });
    });

    return { loadAreaSubscription };
};

export const { loadAreaSubscription } = loader();
